import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';

/**
 * Contraparte "sem tipos" do createCrudHooks, para recursos cujo nome só é
 * conhecido em runtime (screens genéricas em `_app/$resource/*`). Sem segurança
 * de tipos aqui é esperado — é o preço de não precisar de página por recurso.
 */
export function useDynamicList(
  resource: string,
  params?: Record<string, any>,
  options?: { refetchInterval?: number },
) {
  return useQuery({
    queryKey: [resource, 'list', params],
    queryFn: async () => {
      const res = await (apiClient as any)[resource].get({ query: params });
      return res.data;
    },
    // Só usado por telas que precisam refletir mudanças feitas por outros usuários sem
    // depender de F5 (ex: Kanban de pedidos) — refetch em background, sem re-render "loading".
    refetchInterval: options?.refetchInterval,
    refetchOnWindowFocus: !!options?.refetchInterval,
  });
}

export function useDynamicDetail(resource: string, id: string) {
  return useQuery({
    queryKey: [resource, 'detail', id],
    queryFn: async () => {
      const res = await (apiClient as any)[resource]({ id }).get();
      return res.data;
    },
    enabled: !!id,
  });
}

export function useDynamicCreate(resource: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (body: Record<string, any>) => {
      const res = await (apiClient as any)[resource].post(body);
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [resource] });
      toast.success('Dados criados com sucesso!');
    },
  });
}

export function useDynamicUpdate(resource: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, any> }) => {
      const res = await (apiClient as any)[resource]({ id }).patch(data);
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [resource] });
      toast.success('Dados atualizados com sucesso!');
    },
    onError: (e: any) => {
      toast.error(e?.message ?? 'Não foi possível atualizar os dados.');
    },
  });
}

export function useDynamicDelete(resource: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await (apiClient as any)[resource]({ id }).delete();
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [resource] });
      toast.success('Dado removido com sucesso!');
    },
    onError: (e: any) => {
      toast.error(e?.message ?? 'Não foi possível remover os dados.');
    },
  });
}
